import { useCallback, useEffect, useRef, useState } from "react";
import { AiOutlineRobot } from "react-icons/ai";
import {
  FaComment,
  FaBolt,
  FaCogs,
  FaMicrophone,
  FaPhone,
  FaTrash,
  FaTrashRestore,
  FaUpload,
  FaUser,
} from "react-icons/fa";
import {
  MdCheck,
  MdClose,
  MdDescription,
  MdTag,
  MdWarning,
} from "react-icons/md";
import { RiTranslate } from "react-icons/ri";
import Switch from "react-switch";
import { useSearchParams } from "react-router-dom";
import { getCoreModels } from "../../services/coreModelService";
import systemSettingsService from "../../services/systemSettingsService";
import twilioSenderService from "../../services/twilioSenderService";
import { systemSettingKeys } from "../settings/defaultSettings";
import { resizeImage } from "../../utils/imageUtil";
import {
  sanitizePhoneInput,
  validateUsPhoneNumber,
} from "../../utils/miscUtils";
import { useToast } from "../common/ToastContext";
import { useConfirmation } from "../common/ConfirmationContext";
import CircularThumbnail from "./CircularThumbnail";
import personaService from "../../services/personaService";

const PT_TOGGLE_SUPPORTED_BOT_IDS = [
  "bot_adk_live_v6",
  "bot_adk_live_v7",
  "bot_adk_live_v8",
];
const supportsPtToggle = (botId) => PT_TOGGLE_SUPPORTED_BOT_IDS.includes(botId);

const PersonaEditor = ({ item, onSave, onClose, onDelete, onRecover }) => {
  const [searchParams] = useSearchParams();
  const nameInputRef = useRef(null);
  const { showToast } = useToast();
  const { openConfirmation } = useConfirmation();
  const isRcsMode = searchParams.get("rcs") === "true";
  const isGecxMode = searchParams.get("gecx") === "true";
  const [editedPersona, setEditedPersona] = useState(
    item
      ? { enable_pt: true, ...item }
      : {
          name: "",
          description: "",
          tags: [],
          channel: {
            phone_number: "",
            voice_supported: false,
            sms_supported: false,
          },
          models: {
            // New channel-specific models (no legacy fields for new personas)
            voice: {
              stt: "",
              generative: "",
              tts: "",
            },
            sms: {
              generative: "",
            },
            preprocessing: {
              generative: "",
            },
          },
          metadata: {},
          has_image: false,
          enable_pt: true,
        },
  );
  const [portraitPreview, setPortraitPreview] = useState(null);
  const [portraitRemoved, setPortraitRemoved] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [portraitError, setPortraitError] = useState(null);
  const [modelOptions, setModelOptions] = useState({
    stt: [],
    generative: [],
    tts: [],
  });
  const portraitInputRef = useRef(null);
  const [hoveredItemIndex, setHoveredItemIndex] = useState(null);
  const [globalPreprocessingModel, setGlobalPreprocessingModel] =
    useState(null);
  const [twilioSenders, setTwilioSenders] = useState([]);

  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleEscapeKey]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, []);

  useEffect(() => {
    if (item && item.models) {
      // If the item doesn't have the new structure, initialize it from legacy fields
      if (
        !item.models.voice &&
        !item.models.sms &&
        !item.models.preprocessing
      ) {
        setEditedPersona((prev) => ({
          ...prev,
          models: {
            // Migrate to new structure only
            voice: {
              stt: item.models.stt || "",
              generative: item.models.generative || "",
              tts: item.models.tts || "",
            },
            sms: {
              generative: item.models.generative || "",
            },
            preprocessing: {
              generative: item.models.generative || "",
            },
          },
        }));
      }
    }
  }, [item]);

  useEffect(() => {
    const loadImage = async () => {
      try {
        // Load portrait if available
        setEditedPersona((prev) => ({
          ...prev,
          has_image: item.has_image,
        }));
        if (item.has_image) {
          setIsLoadingImage(true);
          const url = await personaService.getImageUrl(item.id, item.version);
          setPortraitPreview(url);
          setIsLoadingImage(false);
        }
      } catch (error) {
        console.error("Error loading images:", error);
        setPortraitError("Failed to load portrait image");
        setIsLoadingImage(false);
      }
    };
    loadImage();
  }, [item]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const models = await getCoreModels();
        // hack: inject the nova models if they are not present
        const novaModels = [
          {
            id: "aws-nova-sonic",
            name: "AWS Nova Sonic",
            type: "stt",
            provider: "aws",
            description: "AWS Nova Sonic",
          },
          {
            id: "aws-nova-sonic",
            name: "AWS Nova Sonic",
            type: "generative",
            provider: "aws",
            description: "AWS Nova Sonic",
          },
          {
            id: "aws-tiffany",
            name: "AWS Tiffany",
            type: "tts",
            provider: "aws",
            description: "AWS Tiffany TTS",
          },
          {
            id: "aws-matthew",
            name: "AWS Matthew",
            type: "tts",
            provider: "aws",
            description: "AWS Matthew TTS",
          },
        ];
        if (!models.some((model) => model.id === "aws-nova-sonic")) {
          models.push(...novaModels);
        }
        // end hack
        let filterModels = models;
        const isLocalhost = true;

        if (!isLocalhost) {
          filterModels = models.filter((model) => model.provider === "google");
        }
        const categorize = filterModels.reduce((acc, model) => {
          if (!acc[model.type]) {
            acc[model.type] = [];
          }
          acc[model.type].push({
            label: model.name,
            value: model.id,
            provider: model.provider,
            ...model,
          });
          return acc;
        }, {});
        for (let type in categorize) {
          categorize[type].sort((a, b) => a.label.localeCompare(b.label));
        }
        setModelOptions(categorize);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    const fetchGlobalPreprocessingModel = async () => {
      try {
        const setting = await systemSettingsService.getByKey(
          systemSettingKeys.prompts.preprocessingModel,
        );
        if (setting && setting.content) {
          setGlobalPreprocessingModel(setting.content);
        } else {
          setGlobalPreprocessingModel("Not found");
        }
      } catch (error) {
        console.error("Error fetching global preprocessing model:", error);
        setGlobalPreprocessingModel("Not found (Error fetching)");
      }
    };
    fetchGlobalPreprocessingModel();
  }, []);

  useEffect(() => {
    const fetchTwilioSenders = async () => {
      if (!isRcsMode) {
        return;
      }

      try {
        const senders = await twilioSenderService.getTwilioSenders();
        setTwilioSenders(senders || []);
      } catch (error) {
        console.error("Error fetching Twilio senders:", error);
        showToast("Failed to fetch Twilio senders", "error");
      }
    };

    fetchTwilioSenders();
  }, [isRcsMode, showToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    // Special case for aws-nova-sonic in voice models
    if (name === "models.voice.generative" && newValue === "aws-nova-sonic") {
      setEditedPersona((prev) => {
        const updatedPersona = { ...prev };
        // Set the generative model
        if (!updatedPersona.models) updatedPersona.models = {};
        if (!updatedPersona.models.voice) updatedPersona.models.voice = {};
        updatedPersona.models.voice.generative = newValue;

        // Reset TTS to one of the allowed values if current value isn't compatible
        if (
          updatedPersona.models.voice.tts !== "aws-tiffany" &&
          updatedPersona.models.voice.tts !== "aws-matthew"
        ) {
          updatedPersona.models.voice.tts = "aws-tiffany"; // Default to Tiffany
        }

        // Set STT as it's included in aws-nova-sonic
        updatedPersona.models.voice.stt = "aws-nova-sonic";

        return updatedPersona;
      });
      return;
    }

    // Legacy support for old model structure with aws-nova-sonic
    if (name === "models.generative" && newValue === "aws-nova-sonic") {
      setEditedPersona((prev) => {
        const updatedPersona = { ...prev };
        // Set the generative model
        if (!updatedPersona.models) updatedPersona.models = {};
        updatedPersona.models.generative = newValue;

        // Reset TTS to one of the allowed values if current value isn't compatible
        if (
          updatedPersona.models.tts !== "aws-tiffany" &&
          updatedPersona.models.tts !== "aws-matthew"
        ) {
          updatedPersona.models.tts = "aws-tiffany"; // Default to Tiffany
        }

        // Set STT as it's included in aws-nova-sonic
        updatedPersona.models.stt = "aws-nova-sonic";

        return updatedPersona;
      });
      return;
    }

    if (
      [
        "llm_params.temperature",
        "llm_params.top_p",
        "llm_params.top_k",
      ].includes(name)
    ) {
      if (newValue === "") {
        newValue = null;
      } // Handle empty string for numbers
    }

    // Handle nested paths (e.g., models.voice.stt)
    setEditedPersona((prev) => {
      const updatedPersona = { ...prev };
      if (name.includes(".")) {
        const parts = name.split(".");
        let current = updatedPersona;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] =
          type === "checkbox" ? checked : newValue;
      } else {
        updatedPersona[name] = type === "checkbox" ? checked : newValue;
      }
      if (name === "external_voice_bot_id" && !supportsPtToggle(newValue)) {
        updatedPersona.enable_pt = true;
      }
      return updatedPersona;
    });
  };

  const handlePhoneChange = (e) => {
    setEditedPersona((prev) => ({
      ...prev,
      channel: {
        ...prev.channel,
        phone_number: sanitizePhoneInput(e.target.value),
      },
    }));
  };

  const handleSwitchChange = (checked, _, name) => {
    setEditedPersona((prev) => {
      const updatedPersona = { ...prev };
      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        updatedPersona[parent] = {
          ...updatedPersona[parent],
          [child]: checked,
        };
      } else {
        updatedPersona[name] = checked;
      }

      if (checked && name === "pipecat_enabled") {
        // defaults chosen from Pipecat's default VAD parameters
        updatedPersona.vad_params = updatedPersona.vad_params || {
          confidence: 0.7,
          start_secs: 0.2,
          stop_secs: 0.8,
          min_volume: 0.6,
        };

        updatedPersona.smart_turn_params = updatedPersona.smart_turn_params || {
          enabled: false,
          stop_secs: 3,
        };

        updatedPersona.llm_params = updatedPersona.llm_params || {
          temperature: null,
          top_p: null,
          top_k: null,
        };
      }
      return updatedPersona;
    });
  };

  const handlePortraitUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setIsLoadingImage(true);

        // Validate the file size.
        if (file.size > 7.5 * 1024 * 1024) {
          setPortraitError("Image size must be under 15MB");
          setIsLoadingImage(false);
          return;
        }
        const resizedFile = await resizeImage(file);

        // Process the file into image data.
        const reader = new FileReader();
        reader.onloadend = () => {
          const img = new Image();
          img.onload = async () => {
            setPortraitPreview(reader.result);
            setPortraitError(null);
            setPortraitRemoved(false);
            setEditedPersona((prev) => ({
              ...prev,
              portraitFile: resizedFile,
            }));
            setIsLoadingImage(false);
          };

          img.onerror = () => {
            setPortraitError("Invalid image format");
            setIsLoadingImage(false);
          };

          img.src = reader.result;
        };

        reader.readAsDataURL(resizedFile);
      } catch (error) {
        console.error("Error processing portrait:", error);
        setPortraitError("Failed to process image");
        setIsLoadingImage(false);
      }
    }
  };

  const handleRemovePortrait = () => {
    setPortraitPreview(null);
    setPortraitRemoved(true);
    setEditedPersona((prev) => {
      return {
        ...prev,
        has_image: false,
        portraitFile: null,
      };
    });
  };

  const isDeleted = Boolean(editedPersona.deleted_at);

  const handleDelete = () => {
    openConfirmation({
      title: "Delete Persona",
      message:
        "Are you sure you want to soft-delete this persona?<br/><br/>" +
        "Consequences of soft-deletion:<br/>" +
        "• The persona will remain usable in existing assignments<br/>" +
        "• It won't be available for new assignments<br/>" +
        "• It won't appear on the persona dashboard<br/><br/>" +
        "You can restore the persona later by undeleting it.",
      onConfirm: onDelete,
    });
  };

  const handleRecover = () => {
    openConfirmation({
      title: "Recover Persona",
      message:
        "Are you sure you want to recover this persona?<br/><br/>" +
        "It will be renamed with a restore timestamp and become available " +
        "for new assignments again.",
      onConfirm: () => onRecover(editedPersona),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoadingImage(true);
      const personaToSave = { ...editedPersona };

      if (!personaToSave?.name) {
        showToast("Please name the persona", "error");
        setIsLoadingImage(false);
        return;
      }
      const { formatted: phoneNumber, error: phoneNumberError } =
        validateUsPhoneNumber(personaToSave?.channel?.phone_number);
      if (phoneNumberError) {
        showToast(phoneNumberError, "error");
        setIsLoadingImage(false);
        return;
      }
      personaToSave.channel = {
        ...personaToSave.channel,
        phone_number: phoneNumber,
      };
      if (!personaToSave?.external_voice_bot_id) {
        showToast("Please assign a voice bot", "error");
        setIsLoadingImage(false);
        return;
      }
      if (!personaToSave?.tags) {
        personaToSave.tags = ["new"];
      }

      // Clean up models - if new structure exists, remove legacy fields completely
      if (personaToSave.models?.voice || personaToSave.models?.sms) {
        delete personaToSave.models.stt;
        delete personaToSave.models.generative;
        delete personaToSave.models.tts;
      }

      if (editedPersona?.pipecat_enabled) {
        if (
          editedPersona?.vad_params?.stop_secs > 0.2 &&
          editedPersona?.smart_turn_params?.enabled
        ) {
          const response = window.confirm(
            "VAD Stop secs should not be greater than 0.2 when Smart Turn is enabled. Do you want to continue?",
          );
          if (!response) {
            setIsLoadingImage(false);
            return;
          }
        }

        if (
          editedPersona?.llm_params?.temperature &&
          editedPersona?.llm_params?.temperature > 0.8
        ) {
          const response = window.confirm(
            "Temperature above 0.8 is allowed, but not recommended for prod. Do you want to continue?",
          );
          if (!response) {
            setIsLoadingImage(false);
            return;
          }
        }
      }

      // Pass image-mutation intent to the parent so it can orchestrate
      // metadata + image API calls in the correct order.
      personaToSave.portraitRemoved = portraitRemoved;

      setIsLoadingImage(false);
      await onSave(personaToSave);
    } catch (error) {
      console.error("Error saving persona:", error);
      showToast("Failed to save persona", "error");
      setIsLoadingImage(false);
    }
  };

  const handleMouseEnter = (itemId) => {
    setHoveredItemIndex(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItemIndex(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-auto">
      <style>
        {`
          select option {
            background-color: #374151;
          }
          select option::after {
            content: attr(data-provider);
            opacity: 0.7;
            font-size: 0.8em;
            margin-left: 0.5em;
          }
        `}
      </style>
      <div className="bg-gray-800 rounded-lg w-full max-w-7xl m-4 max-h-screen flex">
        {/* Left panel - Portrait manager */}
        <div className="w-1/3 p-6 flex flex-col">
          <div className="relative w-full aspect-[16/9] min-h-[280px] max-h-[400px] bg-gray-700 rounded-lg overflow-hidden">
            {isLoadingImage ? (
              <div className="flex items-center justify-center h-full w-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            ) : portraitPreview ? (
              <>
                <img
                  src={portraitPreview}
                  alt="Full portrait"
                  className="h-full w-full object-cover"
                  onError={() => setPortraitError("Failed to load image")}
                />
                <div className="absolute bottom-1 right-1">
                  <button
                    type="button"
                    onClick={handleRemovePortrait}
                    className="bg-red-600 text-white p-2 rounded-full hover:bg-red-500"
                    disabled={isLoadingImage}
                    title="Remove image"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-400 h-full relative">
                <button
                  type="button"
                  onClick={() => portraitInputRef.current.click()}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 flex items-center"
                  disabled={isLoadingImage}
                >
                  <FaUpload className="mr-2" />
                  Upload Image
                </button>
              </div>
            )}
            {portraitError && (
              <div className="absolute top-0 left-0 right-0 bg-red-900 bg-opacity-80 text-white p-3 flex items-center">
                <MdWarning className="mr-2" />
                {portraitError}
              </div>
            )}
          </div>
          <input
            type="file"
            ref={portraitInputRef}
            onChange={handlePortraitUpload}
            className="hidden"
            accept="image/jpeg,image/png,image/webp"
          />
          {/* Add the form fields for the persona details in the left panel below the portrait */}
          <div className="mt-6">
            <div className="relative">
              <FaUser className="absolute left-2 top-3 text-gray-400" />
              <input
                ref={nameInputRef}
                type="text"
                name="name"
                value={editedPersona.name}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Name"
              />
            </div>
            <div className="relative mt-4">
              <MdDescription className="absolute left-2 top-3 text-gray-400" />
              <textarea
                name="description"
                value={editedPersona.description ?? ""}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Description"
                rows="3"
              />
            </div>
            <div className="relative">
              <h3 className="text-md font-semibold text-gray-300">
                Version: {editedPersona.version}
              </h3>
            </div>
          </div>
        </div>
        {/* Right panel - Persona details */}
        <div className="w-2/3 p-6 overflow-y-auto max-h-screen">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex items-center space-x-4">
                {portraitPreview ? (
                  <div className="relative w-20 h-20">
                    <CircularThumbnail
                      imageUrl={portraitPreview}
                      cropData={{
                        unit: "%",
                        x1: 35,
                        y1: 25,
                        x2: 65,
                        y2: 55,
                      }}
                      size={80}
                      className="border-2 border-gray-600"
                      alt={editedPersona.name || "Persona"}
                    />
                  </div>
                ) : (
                  <div className="relative w-20 h-20 rounded-full overflow-hidden bg-gray-700">
                    <img
                      src="/default-avatar.png"
                      alt="Default Avatar"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="flex-grow"></div>
              </div>
              {/* Phone and voice/sms options */}
              <div className="col-span-2 grid grid-cols-3 gap-4">
                <div className="relative">
                  <FaPhone className="absolute left-2 top-3 text-gray-400" />
                  <input
                    type="text"
                    name="channel.phone_number"
                    inputMode="tel"
                    maxLength={20}
                    value={editedPersona?.channel?.phone_number || ""}
                    onChange={handlePhoneChange}
                    className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                    placeholder="Phone Number"
                  />
                </div>
                <div className="flex items-center space-x-4 col-span-2">
                  <div className="flex items-center">
                    <FaMicrophone className="text-gray-400 mr-2" />
                    <Switch
                      onChange={(checked) =>
                        handleSwitchChange(
                          checked,
                          null,
                          "channel.voice_supported",
                        )
                      }
                      checked={editedPersona?.channel?.voice_supported ?? false}
                      onColor="#4299e1"
                      offColor="#718096"
                      height={20}
                      width={40}
                    />
                  </div>
                  <div className="flex items-center">
                    <FaComment className="text-gray-400 mr-2" />
                    <Switch
                      onChange={(checked) =>
                        handleSwitchChange(
                          checked,
                          null,
                          "channel.sms_supported",
                        )
                      }
                      checked={editedPersona?.channel?.sms_supported ?? false}
                      onColor="#4299e1"
                      offColor="#718096"
                      height={20}
                      width={40}
                    />
                  </div>
                </div>
              </div>

              {/* Models section - Split into Voice/SMS/Preprocessing */}
              <div className="col-span-2 space-y-4">
                {/* External Voice Bot Selector - First Option */}
                {editedPersona?.channel?.voice_supported && (
                  <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                    <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                      <AiOutlineRobot className="mr-2 text-white" />
                      <h4 className="text-sm text-gray-200">
                        External Voice Bot
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative">
                        <AiOutlineRobot className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="external_voice_bot_id"
                          value={editedPersona?.external_voice_bot_id || ""}
                          onChange={handleChange}
                          className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                          title="Select an external voice bot service (optional)"
                        >
                          <option value="bot_adk_live_v3">
                            Native Audio V3 (bot_adk_live_v3)
                          </option>
                          <option value="bot_adk_live_v4">
                            Native Audio V4 (bot_adk_live_v4)
                          </option>
                          <option value="bot_adk_live_v5">
                            Native Audio V5 (bot_adk_live_v5)
                          </option>
                          <option value="bot_adk_live_v6">
                            Native Audio V6 (bot_adk_live_v6)
                          </option>
                          <option value="bot_adk_live_v7">
                            Native Audio V7 (bot_adk_live_v7)
                          </option>
                          <option value="bot_adk_live_v8">
                            Native Audio V8 (bot_adk_live_v8)
                          </option>
                        </select>
                      </div>
                      <div className="relative">
                        <FaBolt className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="enable_pt"
                          value={
                            editedPersona?.enable_pt === true ||
                            editedPersona?.enable_pt === "true"
                              ? "true"
                              : "false"
                          }
                          onChange={(e) =>
                            setEditedPersona((prev) => ({
                              ...prev,
                              enable_pt: e.target.value === "true",
                            }))
                          }
                          className={`w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none ${
                            !supportsPtToggle(
                              editedPersona?.external_voice_bot_id,
                            )
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            !supportsPtToggle(
                              editedPersona?.external_voice_bot_id,
                            )
                          }
                          title={
                            !supportsPtToggle(
                              editedPersona?.external_voice_bot_id,
                            )
                              ? `PT can only be disabled on ${PT_TOGGLE_SUPPORTED_BOT_IDS.join(", ")}`
                              : "Enable PT"
                          }
                        >
                          <option value="true">PT Enabled</option>
                          <option value="false">PT Disabled</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {isGecxMode && editedPersona?.channel?.voice_supported && (
                  <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                    <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                      <FaCogs className="mr-2 text-white" />
                      <h4 className="text-sm text-gray-200">Voice Gateway</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="relative">
                        <FaCogs className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="external_voice_gateway_id"
                          value={editedPersona?.external_voice_gateway_id || ""}
                          onChange={handleChange}
                          className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                          title="Voice gateway bot used for GECX topics"
                        >
                          <option value="">None</option>
                          <option value="bot_voice_gateway">
                            Voice Gateway (bot_voice_gateway)
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Voice Models - Only show if no external bot selected */}
                {editedPersona?.channel?.voice_supported && false && (
                  <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                    <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                      <FaPhone className="mr-2 text-white" />
                      <h4 className="text-sm text-gray-200">Voice Models</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative">
                        <RiTranslate className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="models.voice.stt"
                          value={editedPersona?.models?.voice?.stt || ""}
                          onChange={handleChange}
                          className={`w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none ${
                            editedPersona?.models?.voice?.generative ===
                            "aws-nova-sonic"
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            editedPersona?.models?.voice?.generative ===
                            "aws-nova-sonic"
                          }
                          title={
                            editedPersona?.models?.voice?.generative ===
                            "aws-nova-sonic"
                              ? "STT is included in AWS Nova Sonic"
                              : "Speech-to-Text Model"
                          }
                        >
                          <option value="">Select STT</option>
                          {modelOptions.stt &&
                            modelOptions.stt
                              .filter(
                                (option) =>
                                  editedPersona?.models?.voice?.generative ===
                                    "aws-nova-sonic" ||
                                  option.value !== "aws-nova-sonic",
                              )
                              .map((option) => (
                                <option
                                  key={option.value}
                                  value={option.value}
                                  className="bg-gray-700 text-white py-1"
                                >
                                  {option.label} ({option.provider})
                                </option>
                              ))}
                        </select>
                      </div>
                      <div className="relative">
                        <AiOutlineRobot className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="models.voice.generative"
                          value={editedPersona?.models?.voice?.generative || ""}
                          onChange={handleChange}
                          className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                          title="Generative AI Model"
                        >
                          <option value="">Select Generative</option>
                          {modelOptions.generative &&
                            modelOptions.generative.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                className="bg-gray-700 text-white py-1"
                              >
                                {option.label} ({option.provider})
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* TTS Model - Always show when voice is supported */}
                {editedPersona?.channel?.voice_supported && (
                  <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                    <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                      <FaMicrophone className="mr-2 text-white" />
                      <h4 className="text-sm text-gray-200">TTS Model</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="relative">
                        <FaMicrophone className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="models.voice.tts"
                          value={editedPersona?.models?.voice?.tts || ""}
                          onChange={handleChange}
                          className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                          title="Text-to-Speech Model"
                        >
                          <option value="">Select TTS</option>
                          {modelOptions.tts
                            ?.filter((option) => {
                              if (
                                editedPersona?.models?.voice?.generative ===
                                "aws-nova-sonic"
                              ) {
                                return (
                                  option.value === "aws-tiffany" ||
                                  option.value === "aws-matthew"
                                );
                              }
                              return (
                                option.value !== "aws-tiffany" &&
                                option.value !== "aws-matthew"
                              );
                            })
                            .map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                className="bg-gray-700 text-white py-1"
                              >
                                {option.label} ({option.provider})
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* SMS Models */}
                {editedPersona?.channel?.sms_supported && (
                  <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                    <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                      <FaComment className="mr-2 text-white" />
                      <h4 className="text-sm text-gray-200">SMS Models</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="relative">
                        <AiOutlineRobot className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="models.sms.generative"
                          value={editedPersona?.models?.sms?.generative || ""}
                          onChange={handleChange}
                          className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                          title="Generative AI Model for SMS"
                        >
                          <option value="">Select Generative</option>
                          {modelOptions.generative &&
                            modelOptions.generative.map((option) => (
                              <option
                                key={option.value}
                                value={option.value}
                                className="bg-gray-700 text-white py-1"
                              >
                                {option.label} ({option.provider})
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {isRcsMode && editedPersona?.channel?.sms_supported && (
                  <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                    <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                      <FaComment className="mr-2 text-white" />
                      <h4 className="text-sm text-gray-200">RCS Sender</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="relative">
                        <FaComment className="absolute left-2 top-3 text-gray-400" />
                        <select
                          name="twilio_sender_id"
                          value={editedPersona?.twilio_sender_id || ""}
                          onChange={handleChange}
                          className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                          title="Select Twilio sender for RCS"
                        >
                          <option value="">None</option>
                          {twilioSenders.map((sender) => (
                            <option key={sender.id} value={sender.id}>
                              {sender.name || sender.id}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preprocessing Models */}
                <div className="relative rounded-lg p-4 pt-6 border border-gray-600 bg-gray-750">
                  <div className="absolute -top-3 left-4 bg-gray-800 px-2 flex items-center">
                    <AiOutlineRobot className="mr-2 text-white" />
                    <h4 className="text-sm text-gray-200">Preprocessing</h4>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="relative">
                      <AiOutlineRobot className="absolute left-2 top-3 text-gray-400" />
                      <h5
                        className="w-full pl-8 p-2 bg-gray-600 text-gray-300 rounded cursor-not-allowed"
                        title="Global preprocessing model (configured in Settings). This model is used for preprocessing
                               prompts across all personas."
                      >
                        {globalPreprocessingModel || "Loading..."}
                      </h5>
                      <div className="mt-2 text-xs text-gray-400">
                        <span className="text-yellow-400">ℹ️</span> This model
                        is globally configured in{" "}
                        <a
                          href="/settings"
                          className="text-blue-400 hover:text-blue-300 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Settings
                        </a>{" "}
                        and applies to all personas for preprocessing tasks.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Tags section */}
              <div className="col-span-2">
                <div className="relative">
                  <MdTag className="absolute left-2 top-3 text-gray-400" />
                  <input
                    type="text"
                    name="tags"
                    value={editedPersona.tags?.join(", ") ?? ""}
                    onChange={(e) =>
                      setEditedPersona((prev) => ({
                        ...prev,
                        tags: e.target.value.split(", "),
                      }))
                    }
                    className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                    placeholder="Tags (comma separated)"
                  />
                </div>
              </div>
              {/* VAD parameters - Only show if no external bot selected */}
              {false && (
                <div className="col-span-2">
                  <h3 className="text-md font-semibold text-gray-300">
                    VAD Parameters
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("confidence")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Confidence
                      </label>
                      <input
                        type="number"
                        name="vad_params.confidence"
                        value={editedPersona.vad_params?.confidence || "0.7"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.01"
                        min="0"
                        max="1"
                      />
                      {hoveredItemIndex === "confidence" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Confidence threshold for speech detection. Higher
                          values make detection more strict. Must be between 0
                          and 1.
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("start_secs")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Start Secs
                      </label>
                      <input
                        type="number"
                        name="vad_params.start_secs"
                        value={editedPersona.vad_params?.start_secs || "0.2"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.01"
                        min="0"
                      />
                      {hoveredItemIndex === "start_secs" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Time in seconds that speech must be detected before
                          transitioning to SPEAKING state.
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("stop_secs")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Stop Secs
                      </label>
                      <input
                        type="number"
                        name="vad_params.stop_secs"
                        value={editedPersona.vad_params?.stop_secs || "0.8"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.01"
                        min="0"
                      />
                      {hoveredItemIndex === "stop_secs" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Time in seconds of silence required before
                          transitioning back to QUIET state.
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("min_volume")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Min Volume
                      </label>
                      <input
                        type="number"
                        name="vad_params.min_volume"
                        value={editedPersona.vad_params?.min_volume || "0.6"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.01"
                        min="0"
                        max="1"
                      />
                      {hoveredItemIndex === "min_volume" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Minimum audio volume threshold for speech detection.
                          Must be between 0 and 1.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {false && (
                <div className="col-span-2">
                  <h3 className="text-md font-semibold text-gray-300">
                    Smart Turn Parameters
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium mr-2"
                        onMouseEnter={() =>
                          handleMouseEnter("smart_turn_enabled")
                        }
                        onMouseLeave={handleMouseLeave}
                      >
                        Enable Smart Turn
                      </label>
                      <Switch
                        className="mt-1"
                        onChange={(checked) =>
                          handleSwitchChange(
                            checked,
                            null,
                            "smart_turn_params.enabled",
                          )
                        }
                        checked={
                          editedPersona?.smart_turn_params?.enabled ?? false
                        }
                        onColor="#4299e1"
                        offColor="#718096"
                        height={28}
                        width={56}
                      />
                      {hoveredItemIndex === "smart_turn_enabled" && (
                        <div className="absolute bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Enable the smart turn model to predict if the patient
                          has completed their turn. When enabled, VAD stop_secs
                          should be low (0 - 0.2).
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("smart_stop_secs")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Stop Secs
                      </label>
                      <input
                        type="number"
                        name="smart_turn_params.stop_secs"
                        value={
                          editedPersona.smart_turn_params?.stop_secs || "3"
                        }
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.1"
                        min="0.1"
                      />
                      {hoveredItemIndex === "smart_stop_secs" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Time in seconds of silence before overriding smart
                          turn model and initiating a response.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* LLM parameters - Only show if no external bot selected */}
              {false && (
                <div className="col-span-2">
                  <h3 className="text-md font-semibold text-gray-300">
                    LLM Parameters
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("temperature")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Temperature
                      </label>
                      <input
                        type="number"
                        name="llm_params.temperature"
                        value={editedPersona.llm_params?.temperature || "0.0"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.01"
                        min="0"
                        max="1"
                      />
                      {hoveredItemIndex === "temperature" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Optional sampling temperature for output generation
                          randomness. Higher values make the LLM output more
                          diverse. Must be between 0 and 1.
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("top_p")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Top P
                      </label>
                      <input
                        type="number"
                        name="llm_params.top_p"
                        value={editedPersona.llm_params?.top_p || "0.0"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="0.01"
                        min="0"
                        max="1"
                      />
                      {hoveredItemIndex === "top_p" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Optional Top-p sampling parameter for output text
                          diversity. E.g. if top-p is .9, the model will select
                          from the most probable tokens that make up 90% of the
                          probability distribution.
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <label
                        className="block text-gray-300 text-xs font-medium"
                        onMouseEnter={() => handleMouseEnter("top_k")}
                        onMouseLeave={handleMouseLeave}
                      >
                        Top K
                      </label>
                      <input
                        type="number"
                        name="llm_params.top_k"
                        value={editedPersona.llm_params?.top_k || "0"}
                        onChange={handleChange}
                        className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                        step="1"
                        min="0"
                      />
                      {hoveredItemIndex === "top_k" && (
                        <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                          Optional sampling temperature for output text
                          diversity. E.g. if top-k is 50, the model will select
                          from the top 50 most probable tokens.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Additional parameters - Only show if no external bot selected */}
            {false && (
              <div className="col-span-2">
                <h3 className="text-md font-semibold text-gray-300 mt-3">
                  Additional Parameters
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="relative">
                    <label
                      className="block text-gray-300 text-xs font-medium"
                      onMouseEnter={() => handleMouseEnter("turn_timeout")}
                      onMouseLeave={handleMouseLeave}
                    >
                      Turn Timeout
                    </label>
                    <input
                      type="number"
                      name="turn_timeout"
                      value={editedPersona.turn_timeout || "5.0"}
                      onChange={handleChange}
                      className="w-full pl-4 p-2 bg-gray-700 text-white rounded"
                      step="1.0"
                      min="0"
                    />
                    {hoveredItemIndex === "turn_timeout" && (
                      <div className="absolute right-12 top-5 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10">
                        Optional turn timeout for the conversation in seconds.
                        If no user input is detected within this time, the
                        system will reprompt the turn. Default is 5 seconds.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="mt-[50px] flex justify-between items-center">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500 flex items-center"
                  disabled={isLoadingImage}
                >
                  <MdClose className="mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 flex items-center"
                  disabled={isLoadingImage}
                >
                  {isLoadingImage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving ...
                    </>
                  ) : (
                    <>
                      <MdCheck className="mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
              {isDeleted
                ? onRecover &&
                  editedPersona.id && (
                    <button
                      type="button"
                      onClick={handleRecover}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-500 flex items-center"
                      title="Recover Persona"
                      disabled={isLoadingImage}
                    >
                      <FaTrashRestore className="mr-2" />
                      Recover
                    </button>
                  )
                : onDelete &&
                  editedPersona.id && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500 flex items-center"
                      title="Delete Persona"
                      disabled={isLoadingImage}
                    >
                      <FaTrash className="mr-2" />
                      Delete
                    </button>
                  )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PersonaEditor;
